<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Capacitor } from '@capacitor/core';

const props = defineProps({
	threshold: {
		type: Number,
		default: 90
	},
	maxPull: {
		type: Number,
		default: 160
	},
	enabled: {
		type: Boolean,
		default: true
	},
	topOffset: {
		type: String,
		default: '0px'
	}
});

const isTouchDevice = ref(false);
const state = ref('idle'); // 'idle' | 'pulling' | 'refreshing' | 'canceling'
const pullDistance = ref(0); // Visual pull distance in pixels
const progress = computed(() => Math.min(1, pullDistance.value / props.threshold));

// Only enable pull-to-refresh inside Capacitor webview or when running as an iOS standalone PWA
const shouldEnable = computed(() => {
	if (!props.enabled) return false;

	const isCapacitor = Capacitor.isNativePlatform();

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
	              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	const isStandalone = window.navigator.standalone === true ||
	                     window.matchMedia('(display-mode: standalone)').matches;
	const isIOSPWA = isIOS && isStandalone;

	return isCapacitor || isIOSPWA;
});

// Check if the current device/browser supports touch events
const checkTouchDevice = () => {
	isTouchDevice.value = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

// Check if the touch target is inside a modal or a scrollable element (other than body/html)
const isInsideScrollableOrModal = (element) => {
	let current = element;
	while (current && current !== document.body && current !== document.documentElement) {
		// Detect modals, dialogs, wrappers or overlays
		if (
			current.classList.contains('vm-wrapper') ||
			current.classList.contains('vm-modal') ||
			current.classList.contains('modal') ||
			current.getAttribute('role') === 'dialog' ||
			current.classList.contains('dialog') ||
			current.classList.contains('popup') ||
			current.classList.contains('sidebar')
		) {
			return true;
		}

		// Detect scrollable containers (overflow-y auto/scroll with active scroll height)
		const style = window.getComputedStyle(current);
		const overflowY = style.getPropertyValue('overflow-y');
		if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
			return true;
		}

		current = current.parentElement;
	}
	return false;
};

// Gesture tracking variables
let startY = 0;
let startX = 0;
let isTracking = false;
let isFirstMove = true;

const handleTouchStart = (e) => {
	if (!props.enabled || state.value === 'refreshing' || state.value === 'canceling') return;

	// Only activate when page is scrolled to the absolute top
	if (window.scrollY > 0) return;

	// Prevent multi-touch gesture interference
	if (e.touches.length > 1) return;

	// Do not trigger inside modals, dialogs, or sub-scrollable components
	if (e.target && isInsideScrollableOrModal(e.target)) return;

	const touch = e.touches[0];
	startY = touch.clientY;
	startX = touch.clientX;
	isTracking = true;
	isFirstMove = true;
	pullDistance.value = 0;
};

const handleTouchMove = (e) => {
	if (!isTracking || state.value === 'refreshing' || state.value === 'canceling') return;

	const touch = e.touches[0];
	const diffY = touch.clientY - startY;
	const diffX = touch.clientX - startX;

	// Cancel if we are scrolling up or page is not at top
	if (diffY <= 0 || window.scrollY > 0) {
		isTracking = false;
		pullDistance.value = 0;
		state.value = 'idle';
		return;
	}

	// Check swipe angle on first move to prevent horizontal conflicts (e.g., image sliders)
	if (isFirstMove) {
		isFirstMove = false;
		if (Math.abs(diffX) > Math.abs(diffY)) {
			isTracking = false;
			return;
		}
	}

	// Prevent default browser elastic scroll/bounce effect (critical for iOS PWA/standalone mode)
	if (e.cancelable) {
		e.preventDefault();
	}

	// Apply rubber-band damping physics for a native feel
	const dampedDistance = Math.min(props.maxPull, Math.pow(diffY, 0.8) * 2.2);
	pullDistance.value = dampedDistance;
	state.value = 'pulling';
};

const handleTouchEnd = () => {
	if (!isTracking) return;
	isTracking = false;

	if (pullDistance.value >= props.threshold) {
		triggerRefresh();
	} else {
		cancelPull();
	}
};

const triggerRefresh = () => {
	state.value = 'refreshing';
	// Sit slightly higher than the full threshold during the reload transition
	pullDistance.value = props.threshold - 20;

	setTimeout(() => {
		window.location.reload();
	}, 400); // Short delay to allow the spinner animation to be visible
};

const cancelPull = () => {
	state.value = 'canceling';
	pullDistance.value = 0;

	setTimeout(() => {
		state.value = 'idle';
	}, 300); // Matches CSS transition duration
};

// Compute dynamic styling for indicator wrapper
const indicatorStyle = computed(() => {
	const y = pullDistance.value;
	const opacity = progress.value;
	const scale = 0.5 + progress.value * 0.5; // Scale from 0.5 to 1.0

	if (state.value === 'idle') {
		return {
			top: props.topOffset,
			transform: 'translateX(-50%) translateY(-100%) scale(0.5)',
			opacity: 0
		};
	}

	return {
		top: props.topOffset,
		transform: `translateX(-50%) translateY(${y}px) scale(${scale})`,
		opacity: opacity
	};
});

// Compute dynamic rotation of the circular arrow
const arrowStyle = computed(() => {
	const rotation = progress.value * 360;
	return {
		transform: `rotate(${rotation}deg)`
	};
});

onMounted(() => {
	checkTouchDevice();

	if (isTouchDevice.value && shouldEnable.value) {
		window.addEventListener('touchstart', handleTouchStart, { passive: false });
		window.addEventListener('touchmove', handleTouchMove, { passive: false });
		window.addEventListener('touchend', handleTouchEnd);
		window.addEventListener('touchcancel', handleTouchEnd);
	}
});

onUnmounted(() => {
	window.removeEventListener('touchstart', handleTouchStart);
	window.removeEventListener('touchmove', handleTouchMove);
	window.removeEventListener('touchend', handleTouchEnd);
	window.removeEventListener('touchcancel', handleTouchEnd);
});
</script>

<template>
	<div
		v-if="isTouchDevice && shouldEnable"
		class="pull-to-refresh-indicator"
		:style="indicatorStyle"
		:class="{ 'refreshing': state === 'refreshing', 'pulling': state === 'pulling' }"
	>
		<div class="ptr-icon-wrapper">
			<svg
				v-if="state === 'pulling' || state === 'idle' || state === 'canceling'"
				class="ptr-arrow"
				:style="arrowStyle"
				viewBox="0 0 24 24"
			>
				<path
					fill="none"
					stroke="currentColor"
					stroke-width="2.5"
					stroke-linecap="round"
					d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l.67-.67"
				/>
			</svg>
			<div v-else class="ptr-spinner"></div>
		</div>
	</div>
</template>

<style scoped>
.pull-to-refresh-indicator {
	position: fixed;
	left: 50%;
	transform: translateX(-50%) translateY(-100%);
	top: 0;
	width: 42px;
	height: 42px;
	border-radius: 50%;
	background: rgba(22, 22, 22, 0.85);
	backdrop-filter: blur(16px) saturate(180%);
	-webkit-backdrop-filter: blur(16px) saturate(180%);
	border: 1px solid rgba(255, 255, 255, 0.12);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 999999;
	pointer-events: none;
	opacity: 0;
	will-change: transform, opacity;
	transition: opacity 0.2s ease;
}

.pull-to-refresh-indicator.pulling {
	transition: opacity 0.15s ease, transform 0.05s linear;
}

.pull-to-refresh-indicator.refreshing {
	transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.15), opacity 0.2s ease;
}

.ptr-icon-wrapper {
	color: var(--primary);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
}

.ptr-arrow {
	width: 100%;
	height: 100%;
	transition: transform 0.05s linear;
}

.ptr-spinner {
	width: 18px;
	height: 18px;
	border: 2.5px solid rgba(255, 255, 255, 0.15);
	border-top-color: var(--primary);
	border-radius: 50%;
	animation: ptr-spin 0.8s linear infinite;
}

@keyframes ptr-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
